import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Dumbbell, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/data/i18n";
import { TEMPLATES, TEMPLATE_CATEGORIES } from "@/data/templates";
import { WorkoutView } from "@/components/WorkoutView";
import { muscleImage } from "@/data/exercises";

export default function StandardTemplates() {
  const { lang } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [cat, setCat] = useState("Tutte");

  if (plan) {
    return (
      <div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-6">
          <button onClick={() => setPlan(null)} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft size={16} /> {tr(lang, "back")}
          </button>
        </div>
        <WorkoutView plan={{ ...plan, id: crypto.randomUUID() }} setPlan={setPlan} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <button onClick={() => navigate("/")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft size={16} /> {tr(lang, "back")}
      </button>
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-800 text-red-400"><LayoutGrid /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold uppercase text-white">{tr(lang, "mode_std_title")}</h1>
          <p className="text-sm text-zinc-500">{TEMPLATES.length} {tr(lang, "mode_std_sub").toLowerCase()} · {tr(lang, "mode_std_desc")}</p>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {TEMPLATE_CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`template-cat-${c}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${cat === c ? "bg-red-500 text-white red-glow" : "border border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-red-500/40"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {TEMPLATES.filter((t) => cat === "Tutte" || t.category === cat).map((t, i) => (
          <motion.button key={t.id} onClick={() => setPlan(t)} data-testid={`template-card-${i}`}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="group overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900/50 text-left transition-all hover:-translate-y-1 hover:border-red-500/40">
            <div className="relative h-36 overflow-hidden">
              <img src={muscleImage(t.target_muscles[0])} alt="" className="h-full w-full object-cover opacity-60 transition-transform group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
              <span className="absolute left-4 top-4 rounded-full bg-red-500 px-2.5 py-0.5 text-xs font-bold text-white">{t.equipment.includes("Corpo") ? "Corpo Libero" : t.equipment.includes("Macchin") ? "Macchinari" : "Bilancieri"}</span>
            </div>
            <div className="p-5">
              <h3 className="font-display text-xl font-bold uppercase text-white">{t.title}</h3>
              <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{t.overview}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1"><Dumbbell size={13} /> {t.goal}</span>
                <span className="inline-flex items-center gap-1"><Users size={13} /> {t.level}</span>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
